import { createContext, useContext, useEffect, useState } from "react";

// How deep a ContentSpace sits in other ContentSpaces: 0 at the top of a
// module, 1 inside another one's detail pane (an organization or location
// open), and so on.
export const ContentSpaceDepthContext = createContext(0);

// Count of NESTED detail panes open right now, shared page-wide so the shell
// can react (the PMS hides its side banner: a detail inside a detail has half
// the page at best, and the banner's 240-300px were what made it not fit).
let openNestedDetails = 0;
const listeners = new Set<() => void>();

function notify() {
    listeners.forEach((listener) => listener());
}

// Called by every ContentSpace with whether its detail pane is visible.
// Returns the depth to hand down to the children.
export function useNestedDetail(isDetailVisible: boolean): number {
    const depth = useContext(ContentSpaceDepthContext);
    const isNestedDetail = isDetailVisible && depth > 0;
    useEffect(() => {
        if (!isNestedDetail) return;
        openNestedDetails += 1;
        notify();
        return () => {
            openNestedDetails -= 1;
            notify();
        };
    }, [isNestedDetail]);
    return depth + 1;
}

export function useIsNestedDetailOpen(): boolean {
    const [isOpen, setIsOpen] = useState(openNestedDetails > 0);
    useEffect(() => {
        const listener = () => setIsOpen(openNestedDetails > 0);
        listeners.add(listener);
        listener();
        return () => { listeners.delete(listener); };
    }, []);
    return isOpen;
}
